import { RefreshCw, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useAppUpdate } from '../hooks/useAppUpdate'
import { useStandaloneMode } from '../hooks/useStandaloneMode'

/**
 * Banner that prompts the user to refresh when a newer service worker has
 * been deployed. Only shown when running as an installed PWA (standalone
 * display-mode), since the whole point of this banner is to surface updates
 * to users who can't easily refresh from a tab — browser users can just
 * refresh themselves and don't need the prompt.
 */
export function UpdateBanner() {
  const { updateAvailable, applyUpdate, dismiss } = useAppUpdate()
  const isStandalone = useStandaloneMode()
  const location = useLocation()

  if (!updateAvailable) return null
  if (!isStandalone) return null
  // Suppress on the workspace page. It has its own bottom-anchored chrome
  // (mobile keyboard bar, panel switcher) that conflicts visually with a
  // floating banner, and workspace is a long-lived session where an in-flight
  // update prompt is disruptive. Users still get the prompt next time they
  // navigate elsewhere, and can always check manually via Settings → About.
  if (location.pathname === '/workspace') return null

  return (
    <div
      className="fixed left-4 right-4 z-50 mx-auto max-w-md rounded-lg border border-primary/30 bg-background/95 px-4 py-3 shadow-lg backdrop-blur-sm sm:left-auto sm:right-4"
      // 16px above the home-indicator safe area — same level the
      // InstallPromptBanner uses on the browser side. (The two banners are
      // mutually exclusive: install shows only outside PWA, update shows
      // only inside it, so there's no co-existence to plan around.)
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <RefreshCw className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">App update available</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Refresh to get the latest version.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={applyUpdate}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary transition-colors"
            >
              Later
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="flex-shrink-0 -mr-1 -mt-1 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label="Dismiss update banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
